// ============================================================================
// DevopsPage.tsx — ПАНЕЛЬ РАЗРАБОТКИ (приложение «Разработка», hydra.devops).
//
// Показывает состояние машин (основная, Hermes, 244): онлайн, RAM/диск,
// активные kilo-* службы и задачи, рабочие копии git (ветка/HEAD/грязь),
// сессии Kilo, открытые MR и проверки GitLab. Данные собирает серверный агент
// и присылает событиями devops.* — страница обновляется live (без поллинга).
// Действия: запуск/остановка задачи (разрешённые сервером юниты kilo-task-*).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle, GitBranch, GitMerge, HardDrive, MemoryStick, PackageOpen, Play, RefreshCw,
  ServerCrash, ShieldOff, Square, Terminal, Wifi, WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { useSecurityAccess } from '@/lib/securityRoles';
import { canManageDevops } from '@/lib/devopsRoles';
import { classifyDevopsError, type DevopsIssue } from '@/lib/devopsErrors';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';

const log = createLogger('DevopsPage');

export interface DevopsNodeInfo {
  code: string; name: string; host: string; sshUser: string; sshPort: number;
  transport: string; repoPath: string; worktreeRoot: string; kiloUrl: string;
  labels: string; maxTasks: number; enabled: boolean; gitlabProject: string;
}
interface DevopsWorktreeInfo { path: string; branch: string; head: string; dirty: number; isMain: boolean }
interface DevopsServiceInfo { name: string; active: string; sub: string; description: string }
interface DevopsSessionInfo { id: string; title: string; directory: string; updatedAt: string }
interface DevopsMRInfo { iid: number; title: string; sourceBranch: string; webUrl: string; state: string; pipelineStatus: string }
interface DevopsPipelineInfo { id: number; status: string; ref: string; sha: string; webUrl: string; createdAt: string }
export interface DevopsStatusInfo {
  nodeCode: string; online: boolean; collectedAt: string; error: string;
  hostname: string; os: string; uptimeSeconds: number;
  memTotal: number; memUsed: number; memPercent: number;
  diskPath: string; diskTotal: number; diskFree: number; diskPercent: number;
  branch: string; head: string; dirtyFiles: number;
  worktrees: DevopsWorktreeInfo[]; services: DevopsServiceInfo[]; sessions: DevopsSessionInfo[];
  mergeRequests: DevopsMRInfo[]; pipelines: DevopsPipelineInfo[]; errors: string[];
}
export interface DevopsTaskInfo {
  id: string; title: string; nodeCode: string; worktree: string; branch: string;
  status: string; sessionId: string; mrUrl: string; pipelineStatus: string;
  comment: string; kind: string; startedAt: string; finishedAt: string;
  lastError: string; live: boolean;
}

/** 64-битные поля protobuf-es приходят bigint'ом — приводим к number. */
function num(v: unknown): number {
  if (typeof v === 'bigint') return Number(v);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** normalizeStatus — единая нормализация снимка (RPC bigint и событие-JSON). */
function normalizeStatus(raw: unknown): DevopsStatusInfo {
  const r = (raw || {}) as Record<string, unknown>;
  const pipelines: Record<string, unknown>[] = Array.isArray(r.pipelines)
    ? (r.pipelines as Record<string, unknown>[])
    : [];
  return {
    nodeCode: String(r.nodeCode ?? ''),
    online: !!r.online,
    collectedAt: String(r.collectedAt ?? ''),
    error: String(r.error ?? ''),
    hostname: String(r.hostname ?? ''),
    os: String(r.os ?? ''),
    uptimeSeconds: num(r.uptimeSeconds),
    memTotal: num(r.memTotal),
    memUsed: num(r.memUsed),
    memPercent: num(r.memPercent),
    diskPath: String(r.diskPath ?? ''),
    diskTotal: num(r.diskTotal),
    diskFree: num(r.diskFree),
    diskPercent: num(r.diskPercent),
    branch: String(r.branch ?? ''),
    head: String(r.head ?? ''),
    dirtyFiles: num(r.dirtyFiles),
    worktrees: (r.worktrees as DevopsWorktreeInfo[]) || [],
    services: (r.services as DevopsServiceInfo[]) || [],
    sessions: (r.sessions as DevopsSessionInfo[]) || [],
    mergeRequests: (r.mergeRequests as DevopsMRInfo[]) || [],
    pipelines: pipelines.map((p) => ({ ...p, id: num(p.id) })) as unknown as DevopsPipelineInfo[],
    errors: (r.errors as string[]) || [],
  };
}

const STATUS_LABELS: Record<string, string> = {
  new: 'Новая', running: 'Выполняется', stopped: 'Остановлена',
  done: 'Выполнена', error: 'Ошибка',
};
const STATUS_CLASSES: Record<string, string> = {
  running: 'text-primary', done: 'text-green-600', error: 'text-destructive',
  stopped: 'text-muted-foreground', new: 'text-muted-foreground',
};

function fmtBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  const gb = bytes / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(1)} ГБ`;
  return `${(bytes / (1024 ** 2)).toFixed(0)} МБ`;
}

function fmtUptime(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} д ${h} ч`;
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
}

function baseName(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts[parts.length - 1] || path;
}

function Progress({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const color = pct >= 90 ? 'bg-destructive' : pct >= 75 ? 'bg-amber-500' : 'bg-primary';
  return (
    <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: 'ok' | 'bad' | 'warn' | 'muted' }) {
  const cls = tone === 'ok' ? 'bg-green-500/10 text-green-600'
    : tone === 'bad' ? 'bg-destructive/10 text-destructive'
      : tone === 'warn' ? 'bg-amber-500/10 text-amber-600'
        : 'bg-muted text-muted-foreground';
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>;
}

/** NodeCard — карточка узла со снимком состояния. */
function NodeCard({ node, status, busy, onRefresh }: {
  node: DevopsNodeInfo;
  status?: DevopsStatusInfo;
  busy: boolean;
  onRefresh: (code: string) => void;
}) {
  const online = !!status?.online;
  const activeServices = (status?.services || []).filter((s) => s.active === 'active' || s.active === 'activating');
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              {online ? <Wifi className="h-4 w-4 text-green-600" /> : <WifiOff className="h-4 w-4 text-destructive" />}
              <span className="truncate">{node.name || node.code}</span>
            </CardTitle>
            <div className="mt-1 truncate text-xs text-muted-foreground">
              {node.transport === 'local' ? 'эта машина' : `${node.sshUser}@${node.host}:${node.sshPort}`}
              {node.labels ? ` · ${node.labels}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {status?.collectedAt && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(status.collectedAt).toLocaleTimeString('ru-RU')}
              </span>
            )}
            <Button size="icon" variant="ghost" title="Обновить узел" disabled={busy} onClick={() => onRefresh(node.code)}>
              <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 text-xs">
        {status?.error && (
          <div className="flex items-start gap-1.5 rounded border border-destructive/30 bg-destructive/5 p-2 text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="break-words">{status.error}</span>
          </div>
        )}
        {!status && <div className="text-muted-foreground">Снимок ещё не получен — нажмите «Обновить».</div>}
        {status && !status.error && (
          <>
            <div className="space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Память</span><span>{fmtBytes(status.memUsed)} / {fmtBytes(status.memTotal)}</span></div>
              <Progress value={status.memPercent} />
            </div>
            <div className="space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Диск</span><span>{fmtBytes(status.diskTotal - status.diskFree)} / {fmtBytes(status.diskTotal)}</span></div>
              <Progress value={status.diskPercent} />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
              <span className="inline-flex items-center gap-1"><GitBranch className="h-3.5 w-3.5" />{status.branch || '—'} @ {status.head || '—'}</span>
              <Badge tone={status.dirtyFiles > 0 ? 'warn' : 'ok'}>{status.dirtyFiles > 0 ? `изменено ${status.dirtyFiles}` : 'чисто'}</Badge>
              <span className="inline-flex items-center gap-1"><MemoryStick className="h-3.5 w-3.5" />{status.hostname || node.code} · {fmtUptime(Number(status.uptimeSeconds))}</span>
            </div>

            {status.worktrees?.length > 0 && (
              <div className="space-y-1">
                <div className="text-muted-foreground">Рабочие копии ({status.worktrees.length})</div>
                {status.worktrees.slice(0, 6).map((wt) => (
                  <div key={wt.path} className="flex items-center justify-between gap-2 rounded bg-muted/40 px-2 py-1">
                    <span className="truncate" title={wt.path}>{baseName(wt.path)} <span className="text-muted-foreground">{wt.branch || '(detached)'}</span></span>
                    {wt.dirty > 0 ? <Badge tone="warn">{wt.dirty}</Badge> : <Badge tone="ok">чисто</Badge>}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1">
              <div className="text-muted-foreground">Службы kilo ({activeServices.length} активных)</div>
              {status.services?.length ? (
                <div className="flex flex-wrap gap-1">
                  {status.services.slice(0, 8).map((s) => (
                    <Badge key={s.name} tone={s.active === 'active' || s.active === 'activating' ? 'ok' : 'muted'}>
                      {s.name}
                    </Badge>
                  ))}
                </div>
              ) : <div className="text-muted-foreground">нет</div>}
            </div>

            <div className="space-y-1">
              <div className="text-muted-foreground">Сессии Kilo ({status.sessions?.length || 0})</div>
              {status.sessions?.length ? (
                <ul className="space-y-0.5">
                  {status.sessions.slice(0, 4).map((s) => (
                    <li key={s.id} className="truncate" title={`${s.id} · ${s.directory || ''}`}>
                      <Terminal className="mr-1 inline h-3 w-3" />{s.title || s.id}
                    </li>
                  ))}
                </ul>
              ) : <div className="text-muted-foreground">нет</div>}
            </div>

            {status.errors?.length > 0 && (
              <div className="space-y-0.5 rounded border border-amber-500/30 bg-amber-500/5 p-2 text-amber-700">
                {status.errors.map((e) => <div key={e} className="break-words">· {e}</div>)}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** TaskRow — строка задачи с разрешёнными действиями. */
function TaskRow({ task, canManage, busy, onStart, onStop }: {
  task: DevopsTaskInfo; canManage: boolean; busy: boolean;
  onStart: (id: string) => void; onStop: (id: string) => void;
}) {
  const running = task.live || task.status === 'running';
  return (
    <div className="flex flex-col gap-2 border-b px-3 py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{task.title || '(без темы)'}</span>
          <span className={`text-xs ${STATUS_CLASSES[task.status] || 'text-muted-foreground'}`}>
            {STATUS_LABELS[task.status] || task.status || '—'}
          </span>
          {task.live && <span className="h-2 w-2 rounded-full bg-green-500" title="выполняется на узле" />}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
          <span>{task.nodeCode || 'узел не задан'}</span>
          <span>{task.branch || 'ветка не задана'}</span>
          <span className="truncate" title={task.worktree}>{task.worktree || 'рабочая копия не задана'}</span>
          {task.mrUrl && <a className="text-primary hover:underline" href={task.mrUrl} target="_blank" rel="noreferrer">MR</a>}
          {task.pipelineStatus && <span>проверки: {task.pipelineStatus}</span>}
        </div>
        {task.lastError && <div className="mt-1 break-words text-xs text-destructive">{task.lastError}</div>}
      </div>
      {canManage && (
        <div className="flex shrink-0 items-center gap-2">
          {running ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onStop(task.id)}>
              <Square className="mr-1 h-3.5 w-3.5" />Остановить
            </Button>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => onStart(task.id)}>
              <Play className="mr-1 h-3.5 w-3.5" />Запустить
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** DevopsUnavailable — понятное состояние вместо сырого 502/400. */
function DevopsUnavailable({ issue, onRetry }: { issue: DevopsIssue; onRetry: () => void }) {
  const Icon = issue.kind === 'not_installed' ? PackageOpen
    : issue.kind === 'forbidden' ? ShieldOff
      : ServerCrash;
  return (
    <div className="mx-auto max-w-lg rounded-lg border bg-card p-6 text-center">
      <Icon className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
      <div className="text-base font-semibold">{issue.title}</div>
      <div className="mt-2 text-sm text-muted-foreground">{issue.detail}</div>
      <Button className="mt-4" variant="outline" onClick={onRetry}>
        <RefreshCw className="mr-1 h-4 w-4" />Повторить
      </Button>
    </div>
  );
}

export function DevopsPage() {
  const access = useSecurityAccess();
  const canManage = canManageDevops(access);
  const showError = (m: string) => useToastStore.getState().showError(m);
  const showSuccess = (m: string) => useToastStore.getState().showSuccess(m);

  const [nodes, setNodes] = useState<DevopsNodeInfo[]>([]);
  const [statuses, setStatuses] = useState<Record<string, DevopsStatusInfo>>({});
  const [tasks, setTasks] = useState<DevopsTaskInfo[]>([]);
  const [gitlab, setGitlab] = useState<{ configured: boolean; error: string }>({ configured: false, error: '' });
  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<DevopsIssue | null>(null);
  const [busy, setBusy] = useState('');

  const loadNodes = useCallback(async () => {
    try {
      const nodesResp = await getSDK().devops.listNodes({});
      const n = (nodesResp.nodes || []) as DevopsNodeInfo[];
      const map: Record<string, DevopsStatusInfo> = {};
      for (const st of (nodesResp.statuses || []) as unknown[]) {
        const norm = normalizeStatus(st);
        map[norm.nodeCode] = norm;
      }
      setNodes(n);
      setStatuses(map);
      setGitlab({ configured: !!nodesResp.gitlabConfigured, error: nodesResp.gitlabError || '' });
      setIssue(null);
    } catch (e) {
      log.error('load devops nodes', { error: e });
      setIssue(classifyDevopsError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const r = await getSDK().devops.listTasks({});
      setTasks((r.tasks || []) as DevopsTaskInfo[]);
    } catch (e) {
      log.error('reload devops tasks', { error: e });
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadNodes(), loadTasks()]);
  }, [loadNodes, loadTasks]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  // LIVE: снимок узла приходит событием (payload — JSON Status).
  useEffect(() => eventBusHub.subscribe((ev) => {
    if (ev.topic.startsWith('devops.node.status.')) {
      const st = normalizeStatus(ev.payload);
      if (st.nodeCode) setStatuses((prev) => ({ ...prev, [st.nodeCode]: st }));
      return;
    }
    if (ev.topic === 'devops.nodes.updated') {
      void loadNodes();
      return;
    }
    if (ev.topic === 'devops.tasks.updated') {
      void loadTasks();
    }
  }), [loadNodes, loadTasks]);

  const refreshNode = async (code: string) => {
    setBusy(`node:${code}`);
    try {
      await getSDK().devops.refreshNode({ nodeCode: code });
      showSuccess('Снимок узла обновлён');
    } catch (e) {
      showError(friendlyError(e) || 'Не удалось обновить узел');
    } finally {
      setBusy('');
    }
  };

  const startTask = async (taskId: string) => {
    setBusy(`task:${taskId}`);
    try {
      await getSDK().devops.startTask({ taskId });
      showSuccess('Задача запущена');
      await loadTasks();
    } catch (e) {
      showError(friendlyError(e) || 'Не удалось запустить задачу');
      await loadTasks();
    } finally {
      setBusy('');
    }
  };

  const stopTask = async (taskId: string) => {
    setBusy(`task:${taskId}`);
    try {
      await getSDK().devops.stopTask({ taskId });
      showSuccess('Задача остановлена');
      await loadTasks();
    } catch (e) {
      showError(friendlyError(e) || 'Не удалось остановить задачу');
      await loadTasks();
    } finally {
      setBusy('');
    }
  };

  const mergeRequests = useMemo(() => {
    const first = Object.values(statuses).find((st) => (st.mergeRequests || []).length > 0);
    return first?.mergeRequests || [];
  }, [statuses]);
  const pipelines = useMemo(() => {
    const first = Object.values(statuses).find((st) => (st.pipelines || []).length > 0);
    return first?.pipelines || [];
  }, [statuses]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <div className="text-base font-semibold">Разработка</div>
          <div className="text-xs text-muted-foreground">
            Узлы, задачи и проверки · обновление серверным агентом каждые 10–15 с
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!issue && (gitlab.configured ? (
            <Badge tone="ok"><GitMerge className="mr-1 inline h-3 w-3" />GitLab подключён</Badge>
          ) : (
            <Badge tone="warn">GitLab не настроен</Badge>
          ))}
          <Button size="sm" variant="outline" onClick={() => void loadAll()} disabled={loading}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Обновить
          </Button>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-auto p-4">
        {issue && nodes.length > 0 && (
          <div className="flex items-center justify-between gap-2 rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700">
            <span><span className="font-medium">{issue.title}.</span> {issue.detail}</span>
            <Button size="sm" variant="outline" onClick={() => void loadAll()}>Повторить</Button>
          </div>
        )}
        {!gitlab.configured && gitlab.error && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700">{gitlab.error}</div>
        )}

        {loading && nodes.length === 0 ? (
          <div className="text-sm text-muted-foreground">Загрузка панели…</div>
        ) : nodes.length === 0 ? (
          issue ? (
            <DevopsUnavailable issue={issue} onRetry={() => void loadAll()} />
          ) : (
            <div className="rounded border p-6 text-sm text-muted-foreground">
              Узлы разработки не настроены. Создайте их в разделе «Разработка» → «Узлы разработки»
              (или примените поставку hydra.devops в пустую базу).
            </div>
          )
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {nodes.map((node) => (
              <NodeCard
                key={node.code}
                node={node}
                status={statuses[node.code]}
                busy={busy === `node:${node.code}`}
                onRefresh={(code) => void refreshNode(code)}
              />
            ))}
          </div>
        )}

        {(mergeRequests.length > 0 || pipelines.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm"><GitMerge className="h-4 w-4" />GitLab: открытые MR и проверки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {mergeRequests.map((mr) => (
                <div key={mr.iid} className="flex flex-wrap items-center gap-2">
                  <a className="text-primary hover:underline" href={mr.webUrl} target="_blank" rel="noreferrer">!{mr.iid}</a>
                  <span className="truncate">{mr.title}</span>
                  <Badge tone={mr.pipelineStatus === 'success' ? 'ok' : mr.pipelineStatus === 'failed' ? 'bad' : 'muted'}>
                    {mr.sourceBranch}{mr.pipelineStatus ? ` · ${mr.pipelineStatus}` : ''}
                  </Badge>
                </div>
              ))}
              {pipelines.length > 0 && (
                <div className="flex flex-wrap gap-2 text-muted-foreground">
                  {pipelines.slice(0, 6).map((p) => (
                    <a key={String(p.id)} href={p.webUrl} target="_blank" rel="noreferrer" className="hover:underline">
                      <Badge tone={p.status === 'success' ? 'ok' : p.status === 'failed' ? 'bad' : 'muted'}>
                        {p.ref || p.sha?.slice(0, 8)}: {p.status}
                      </Badge>
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between gap-2 text-sm">
              <span>Задачи разработки ({tasks.length})</span>
              <span className="text-xs font-normal text-muted-foreground">
                Запуск: Kilo в рабочей копии под юнитом kilo-task-&lt;id&gt;
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {tasks.length === 0 ? (
              <div className="px-4 py-6 text-xs text-muted-foreground">
                Задач нет. Создайте задачу в разделе «Разработка» → «Задачи разработки»:
                тема, узел, рабочая копия и ветка.
              </div>
            ) : (
              <div className="max-h-[45vh] overflow-auto">
                {tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    canManage={canManage}
                    busy={busy === `task:${task.id}`}
                    onStart={(id) => void startTask(id)}
                    onStop={(id) => void stopTask(id)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 pb-4 text-[11px] text-muted-foreground">
          <HardDrive className="h-3.5 w-3.5" />
          Снимки хранятся в dev_node_status; SSH/GitLab-доступ настраивается на сервере
          (HYDRA_DEVOPS_SSH_KEY, HYDRA_DEVOPS_GITLAB_URL/TOKEN или настройки базы).
        </div>
      </div>
    </div>
  );
}
